import React from 'react';

/**
 * Catches render-time errors so a fault in one screen doesn't unmount the whole
 * app. Without a boundary anywhere in the tree, React 18 unmounts the entire root
 * on any uncaught render error and the user is left staring at a blank white page
 * with no indication that anything went wrong.
 *
 * Error boundaries only catch errors thrown during rendering, in lifecycle methods
 * and in constructors below them — not event handlers, async code or SSR.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Keep this a console error rather than a silent swallow: it is the only
    // signal available until an error-reporting service is wired up.
    console.error('Unhandled render error:', error, errorInfo?.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.assign('/');
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          padding: '24px',
          textAlign: 'center',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          background: '#0E0E10',
          color: '#F5F5F5'
        }}
      >
        <h1 style={{ fontSize: '1.5rem', margin: 0 }}>Something went wrong</h1>
        <p style={{ maxWidth: '42ch', opacity: 0.75, margin: 0 }}>
          This page failed to load. Your data is safe — reloading usually resolves it.
        </p>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              padding: '10px 24px',
              borderRadius: '6px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 600,
              background: '#C6A75E',
              color: '#0E0E10'
            }}
          >
            Reload page
          </button>
          <button
            type="button"
            onClick={this.handleGoHome}
            style={{
              padding: '10px 24px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              background: 'transparent',
              color: '#F5F5F5',
              border: '1px solid rgba(245,245,245,0.35)'
            }}
          >
            Go to home
          </button>
        </div>
        {process.env.NODE_ENV !== 'production' && this.state.error && (
          <pre
            style={{
              marginTop: '16px',
              maxWidth: '90vw',
              overflow: 'auto',
              textAlign: 'left',
              fontSize: '0.75rem',
              opacity: 0.7
            }}
          >
            {String(this.state.error?.stack || this.state.error)}
          </pre>
        )}
      </div>
    );
  }
}

export default ErrorBoundary;
